import { formatStringTemplate, isNullOrUndef } from "@civicscience/chops";

import { ErrorMessages } from "consts";

const { ErrUrlInvalid, ErrJsonInvalid, ErrSettingsInvalid, ErrColorInvalid } = ErrorMessages;

const urlPattern = /^(https?:\/\/)([\w-]+(\.[\w-]+)+)(\/[\w.-]*)*(\?.*)?(#.*)?$/;
const colorPattern = /^#([0-9A-Fa-f]{3}){1,2}$/;

/**
 * Validates if a string is a valid URL format.
 * @param value - The string to validate as a URL. Defaults to empty string if not provided.
 * @returns `true` if the URL is valid, otherwise returns an error message string.
 */
export const validateIsUrl = (value: string = ""): boolean | string => {
  const isValid = urlPattern.test(value) === true;
  return isValid || ErrUrlInvalid;
};

/**
 * Validates that all keys in an object are present in a list of allowed values.
 * @param obj - The object to validate keys for.
 * @param values - Array of allowed key names.
 * @param allowEmpty - If true, an empty object is considered valid. Defaults to false.
 * @returns `true` if all object keys are in the allowed values list, `false` otherwise.
 */
export const validateObjectKeys = (obj: unknown, values: string[], allowEmpty: boolean = false): boolean => {
  if (isNullOrUndef(obj) || typeof obj !== "object") return false;
  const objKeys = Object.keys(obj);
  if (objKeys.length === 0 && !allowEmpty) return false;
  return objKeys.every((key) => values.includes(key));
};

/**
 * Validates theme settings JSON string contains only allowed properties.
 * @param value - JSON string containing theme settings. Defaults to empty string if not provided.
 * @returns `true` if settings are valid, otherwise returns an error message string.
 * @throws Returns `ErrJsonInvalid` if the input is not valid JSON.
 */
export const validateThemeSettings = (value: string = ""): boolean | string => {
  const allowedSettings = ["primaryColor", "accentColor", "fontFamily"];
  try {
    const obj = JSON.parse(value);
    return (
      validateObjectKeys(obj, allowedSettings) ||
      formatStringTemplate(ErrSettingsInvalid, { allowedSettings: allowedSettings.join(", ") })
    );
  } catch {
    return ErrJsonInvalid;
  }
};

/**
 * Validates that color values in theme JSON are valid hex color codes.
 * @param value - JSON string containing theme color settings. Defaults to empty string if not provided. Current supported keys are `primaryColor` and `accentColor`.
 * @returns `true` if all color values are valid hex codes, otherwise returns an error message string.
 * @throws Returns `ErrJsonInvalid` if the input is not valid JSON.
 */
export const validateThemeColors = (value: string = ""): boolean | string => {
  try {
    const obj = JSON.parse(value);
    const primaryColorValidation = obj?.primaryColor ? colorPattern.test(obj.primaryColor) === true : true;
    const accentColorValidation = obj?.accentColor ? colorPattern.test(obj.accentColor) === true : true;
    return (primaryColorValidation && accentColorValidation) || ErrColorInvalid;
  } catch {
    return ErrJsonInvalid;
  }
};
